import { formatDateTime } from '@/lib/format';
import type { Job } from '@/papermill/api';

import styles from './RunDetails.module.css';

export interface RunDetailsProps {
  job: Job;
  metrics?: Record<string, unknown> | null;
  manifest?: Record<string, unknown> | null;
  open?: boolean;
}

interface StepTiming {
  step_id: string;
  elapsed_seconds: number;
  cache_hit?: boolean;
}

function asSteps(metrics: Record<string, unknown> | null | undefined): StepTiming[] {
  const steps = metrics?.steps;
  return Array.isArray(steps)
    ? (steps as StepTiming[]).filter((step) => typeof step?.elapsed_seconds === 'number')
    : [];
}

function Row({ term, value }: { term: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return (
    <div className={styles.row}>
      <dt>{term}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** What happened to a job: timings, verdict, failure, and the raw record. */
export function RunDetails({ job, metrics, manifest, open = false }: RunDetailsProps) {
  const steps = asSteps(metrics);
  const longest = Math.max(0.001, ...steps.map((step) => step.elapsed_seconds));
  const total =
    typeof metrics?.total_seconds === 'number' ? (metrics.total_seconds as number) : null;
  const degradation = (manifest?.degradation ?? {}) as Record<string, unknown>;
  const flags = Object.entries(degradation)
    .filter(([, value]) => value === true)
    .map(([key]) => key.replace(/_/g, ' '));
  const log = [
    job.submittedAt ? `${job.submittedAt}  submitted ${job.filename ?? ''}` : null,
    job.phase ? `             last stage ${job.phase}` : null,
    job.failure?.failingStep ? `             failing step ${job.failure.failingStep}` : null,
    job.failure?.attemptCount ? `             attempts ${job.failure.attemptCount}` : null,
    job.failure?.failureClass ? `             failure ${job.failure.failureClass}` : null,
    job.failure?.error ? `             error ${job.failure.error}` : null,
    job.verdict ? `             verdict ${job.verdict}` : null,
    job.completedAt ? `${job.completedAt}  finished with status ${job.status}` : null,
  ].filter(Boolean);

  return (
    <details className={styles.details} open={open}>
      <summary>Run details & logs</summary>
      <div className={styles.body}>
        <dl className={styles.facts}>
          <Row term="Job ID" value={job.id} />
          <Row term="Status" value={job.status} />
          <Row term="Submitted" value={job.submittedAt ? formatDateTime(job.submittedAt) : null} />
          <Row term="Finished" value={job.completedAt ? formatDateTime(job.completedAt) : null} />
          <Row term="Processing time" value={total !== null ? `${total.toFixed(1)}s` : null} />
          <Row term="Verdict" value={job.verdict} />
          <Row term="Pages" value={job.pageCount} />
          <Row term="Credits" value={job.credits} />
          <Row term="Detected type" value={manifest?.sniffed_mime as string | undefined} />
          <Row term="Conversion" value={manifest?.conversion_path as string | undefined} />
          <Row term="Repairs" value={flags.length ? flags.join(', ') : null} />
          <Row term="Failure" value={job.failure?.failureClass} />
          <Row term="Failing step" value={job.failure?.failingStep} />
          <Row term="Attempts" value={job.failure?.attemptCount} />
        </dl>

        {steps.length > 0 ? (
          <div>
            <h3 className={styles.heading}>Pipeline timings</h3>
            <ul className={styles.steps}>
              {steps.map((step) => (
                <li key={step.step_id}>
                  <span className={styles.stepName}>{step.step_id.replace(/_/g, ' ')}</span>
                  <span className={styles.bar} aria-hidden="true">
                    <span style={{ width: `${(step.elapsed_seconds / longest) * 100}%` }} />
                  </span>
                  <span className={styles.stepTime}>
                    {step.elapsed_seconds.toFixed(2)}s{step.cache_hit ? ' · cached' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div>
          <h3 className={styles.heading}>Log</h3>
          <pre className={styles.log}>{log.join('\n') || 'Nothing recorded yet.'}</pre>
        </div>
      </div>
    </details>
  );
}
